import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MDRBadge } from "@/components/MDRBadge";
import { CsvIOButtons } from "@/components/admin/CsvIOButtons";
import { toast } from "sonner";
import { bundleExtendedData, examTypeStatuses, statusLabels } from "@/data/adminConfigData";
import { servicesData, serviceBundlesData } from "@/data/mockData";
import { BookOpen, Plus, Pencil, Trash2, Copy, Calendar } from "lucide-react";

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  testing: "bg-amber-50 text-amber-700 border-amber-200",
  attivo: "bg-emerald-50 text-emerald-700 border-emerald-200",
  non_attivo: "bg-rose-50 text-rose-700 border-rose-200"
};

const buildRows = () => serviceBundlesData.map(b => {
  const ext = bundleExtendedData.find(e => e.bundleId === b.id);
  return {
    id: b.id,
    name: b.name,
    description: b.description,
    services: b.services,
    status: ext?.status || "draft",
    dataAttivazione: ext?.dataAttivazione || "",
    dataScadenza: ext?.dataScadenza || ""
  };
});

const csvColumns = [
  { key: "id", label: "id" },
  { key: "name", label: "nome" },
  { key: "description", label: "descrizione" },
  { key: "services", label: "prestazioni_ids", get: (r) => r.services.join("|"), parse: (v) => v.split("|").map(s => s.trim()).filter(Boolean) },
  { key: "status", label: "stato" },
  { key: "dataAttivazione", label: "data_attivazione" },
  { key: "dataScadenza", label: "data_scadenza" }
];

const empty = { name: "", description: "", services: [], status: "draft", dataAttivazione: "", dataScadenza: "" };

export const AdminBundlesPage = () => {
  const [rows, setRows] = useState(buildRows());
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const startEdit = (r) => { setEditing(r ? { ...r, services: [...r.services] } : { ...empty, id: `bnd${Date.now()}` }); setOpen(true); };

  const clone = (r) => {
    const newId = `${r.id}_copy${Date.now().toString().slice(-4)}`;
    setRows(prev => [...prev, { ...r, id: newId, name: `${r.name} (copia)`, services: [...r.services], status: "draft" }]);
    toast.success("Bundle clonato");
  };

  const save = () => {
    if (!editing.name) { toast.error("Inserire nome bundle"); return; }
    if (editing.services.length === 0) { toast.error("Selezionare almeno una prestazione"); return; }
    setRows(prev => {
      const exists = prev.find(p => p.id === editing.id);
      return exists ? prev.map(p => p.id === editing.id ? editing : p) : [...prev, editing];
    });
    setOpen(false);
    toast.success("Bundle salvato");
  };

  const remove = (id) => {
    setRows(prev => prev.filter(p => p.id !== id));
    toast.success("Bundle rimosso");
  };

  const handleImport = (parsed) => {
    setRows(parsed.map(p => ({ ...empty, ...p, services: Array.isArray(p.services) ? p.services : [] })));
  };

  const toggleService = (sid) => {
    setEditing(e => ({
      ...e,
      services: e.services.includes(sid) ? e.services.filter(x => x !== sid) : [...e.services, sid]
    }));
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <BookOpen className="h-4 w-4 text-slate-600" />
            <span className="text-xs uppercase tracking-wider text-muted-foreground">6.3 Configurazione</span>
            <MDRBadge variant="admin" size="sm" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Bundle</h1>
          <p className="text-sm text-muted-foreground mt-1">Pacchetti di prestazioni vendibili in promozione</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <CsvIOButtons rows={rows} columns={csvColumns} filename="bundles" onImport={handleImport} testIdPrefix="bundles-csv" />
          <Button variant="premium" onClick={() => startEdit(null)} className="gap-2" data-testid="bundles-new-btn">
            <Plus className="h-4 w-4" /> Nuovo bundle
          </Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {rows.map(r => {
          const services = r.services.map(sid => servicesData.find(s => s.id === sid)).filter(Boolean);
          return (
            <Card key={r.id} data-testid={`bundle-card-${r.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base">{r.name}</CardTitle>
                  <Badge variant="outline" className={STATUS_STYLE[r.status]}>{statusLabels[r.status]}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">{r.description}</p>
                <div className="flex flex-wrap gap-1">
                  {services.map(s => <Badge key={s.id} variant="secondary" className="text-[10px]">{s.name}</Badge>)}
                </div>
                <div className="flex flex-wrap gap-3 text-[11px] text-muted-foreground pt-2 border-t">
                  {r.dataAttivazione && <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> Da {r.dataAttivazione}</span>}
                  {r.dataScadenza && <span>→ {r.dataScadenza}</span>}
                </div>
                <div className="flex justify-end gap-1">
                  <Button variant="ghost" size="icon" onClick={() => clone(r)} title="Clona" data-testid={`bundle-clone-${r.id}`}>
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => startEdit(r)} data-testid={`bundle-edit-${r.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="text-destructive" onClick={() => remove(r.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing && rows.find(p => p.id === editing.id) ? "Modifica bundle" : "Nuovo bundle"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label>Nome</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="bundle-form-name" />
              </div>
              <div>
                <Label>Descrizione</Label>
                <Textarea rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
              <div>
                <Label>Prestazioni incluse</Label>
                <div className="grid grid-cols-2 gap-2 mt-1 max-h-48 overflow-y-auto p-2 rounded-md border">
                  {servicesData.map(s => (
                    <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={editing.services.includes(s.id)}
                        onCheckedChange={() => toggleService(s.id)}
                      />
                      <span className="truncate">{s.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label>Stato</Label>
                <select className="w-full mt-1 border rounded-md p-2 text-sm bg-background"
                  value={editing.status}
                  onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                  {examTypeStatuses.map(s => <option key={s} value={s}>{statusLabels[s]}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Data attivazione</Label>
                  <Input type="date" value={editing.dataAttivazione} onChange={(e) => setEditing({ ...editing, dataAttivazione: e.target.value })} />
                </div>
                <div>
                  <Label>Data scadenza</Label>
                  <Input type="date" value={editing.dataScadenza} onChange={(e) => setEditing({ ...editing, dataScadenza: e.target.value })} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="bundle-form-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBundlesPage;
